// ============================================================================
// ActivitySkeleton — first-load placeholders (ORG-USAGE-2026 round 2)
//
// The owner's test found the page sitting on a bare "loading activity…" line
// while the callables cold-started — it read as broken. These skeletons mirror
// the real layout (tiles → chart → breakdowns → table) so the page's shape is
// visible immediately and the wait reads as loading, not failure.
// ============================================================================

import React from 'react';
import styled, { keyframes } from 'styled-components';
import { ACTIVITY_COLORS } from './activityTheme';

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const Block = styled.div<{ $h: string; $w?: string; $r?: string }>`
  height: ${(p) => p.$h};
  width: ${(p) => p.$w ?? '100%'};
  border-radius: ${(p) => p.$r ?? '10px'};
  background: linear-gradient(
    90deg,
    ${ACTIVITY_COLORS.gridline} 25%,
    #f7f7f5 37%,
    ${ACTIVITY_COLORS.gridline} 63%
  );
  background-size: 800px 100%;
  animation: ${shimmer} 1.4s ease-in-out infinite;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const Panel = styled.div`
  border: 1px solid ${ACTIVITY_COLORS.border};
  border-radius: 10px;
  padding: 1.25rem;
  background: ${ACTIVITY_COLORS.surface};
  min-width: 0;
`;

/* Mirrors ActivityOverview's OverviewLayout: chart column + by-model rail on
   wide screens, single column below 1360px. */
const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  align-items: start;

  @media (min-width: 1360px) {
    grid-template-columns: minmax(0, 1fr) clamp(340px, 27vw, 460px);
  }
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 1.5rem;

  @media (min-width: 1360px) {
    grid-column: 1 / -1;
  }
`;

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
`;

const ActivitySkeleton: React.FC = () => (
  <div data-testid="activity-skeleton" aria-busy="true" aria-label="loading activity">
    <Grid>
      {[0, 1, 2, 3].map((i) => (
        <Panel key={i}>
          <Block $h="0.75rem" $w="55%" $r="4px" />
          <div style={{ height: '0.625rem' }} />
          <Block $h="1.5rem" $w="40%" $r="6px" />
        </Panel>
      ))}
    </Grid>

    <Layout>
      <Panel>
        <Block $h="0.875rem" $w="160px" $r="4px" />
        <div style={{ height: '1rem' }} />
        <Block $h="clamp(170px, 24vh, 300px)" $r="8px" />
      </Panel>

      <Panel>
        <Block $h="0.875rem" $w="120px" $r="4px" />
        <div style={{ height: '1rem' }} />
        <Rows>
          <Block $h="0.875rem" $r="4px" />
          <Block $h="0.875rem" $w="85%" $r="4px" />
          <Block $h="0.875rem" $w="70%" $r="4px" />
        </Rows>
      </Panel>

      {/* one breakdown below the chart now — by-member (by-source is gone) */}
      <TwoCol>
        <Panel>
          <Block $h="0.875rem" $w="120px" $r="4px" />
          <div style={{ height: '1rem' }} />
          <Rows>
            <Block $h="0.875rem" $r="4px" />
            <Block $h="0.875rem" $w="85%" $r="4px" />
            <Block $h="0.875rem" $w="70%" $r="4px" />
          </Rows>
        </Panel>
      </TwoCol>
    </Layout>
    {/* R17: no events-table stand-in here — the real table mounts alongside
        this skeleton now (it no longer waits for the summary) and paints its
        own loading rows. */}
  </div>
);

export default ActivitySkeleton;
